/**
 * Release notes, read at build time from `releases/*.md` — the same files
 * `make-update-manifest.mjs` reads for the in-app updater, so the website and
 * the update dialog never disagree. One file per version; see
 * `releases/0.1.3.md` for the shape and `scripts/parse-release-notes.mjs` for
 * the Node-side twin of the parser below (duplicated rather than shared,
 * since this one runs in the browser via `import.meta.glob` and that one
 * reads from `node:fs`).
 *
 * This bundled corpus is the instant, always-available floor: `useReleases`
 * renders it synchronously and then upgrades it from the releases API, so a
 * note edited after the last portal build still shows up without the site
 * having to depend on the API being reachable. The `Release` shape and the
 * version ordering live in `@bigbear/shared` because the API must agree with
 * them.
 */

import { type Release, sortReleases } from "@bigbear/shared";

export type { Release };

function parseReleaseFile(text: string, filename: string): Release {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/.exec(text);
  if (!match) {
    throw new Error(`${filename}: missing --- frontmatter block`);
  }
  const [, frontmatter, body] = match;

  const fields: Record<string, string> = {};
  for (const line of frontmatter!.split(/\r?\n/)) {
    if (!line.trim()) continue;
    const colon = line.indexOf(":");
    if (colon === -1) throw new Error(`${filename}: malformed frontmatter line: ${line}`);
    fields[line.slice(0, colon).trim()] = line.slice(colon + 1).trim();
  }

  for (const key of ["version", "date", "headline"]) {
    if (!fields[key]) throw new Error(`${filename}: frontmatter is missing "${key}"`);
  }

  return { version: fields.version!, date: fields.date!, headline: fields.headline!, body: body!.trim() };
}

const files = import.meta.glob("../../../releases/*.md", { eager: true, query: "?raw", import: "default" }) as Record<
  string,
  string
>;

/** Every release, newest first. */
export const releases: readonly Release[] = sortReleases(
  Object.entries(files).map(([filename, text]) => parseReleaseFile(text, filename)),
);

export const latestRelease: Release | undefined = releases[0];
